import {
  fetchBlinkList2 as fetchBlinkList,
  fetchMetadata,
  useAction,
  useBlink2 as useBlink,
  useBlinkApiUrl,
  useBlinkList2 as useBlinkList,
  useBlinksRegistryInterval,
  useMetadata
} from "./chunk-273CTNRW.js";
import {
  AbstractActionComponent,
  BASELINE_ACTION_VERSION,
  BASELINE_BLINK_BLOCKCHAIN_IDS,
  BLINK_CLIENT_KEY_HEADER,
  BlinkInstance,
  BlinksRegistry,
  BlinksURLMapper,
  BlockchainIds,
  ButtonActionComponent,
  DEFAULT_SUPPORTED_BLOCKCHAIN_IDS,
  EMPTY_OBJECT,
  FormActionComponent,
  MAX_SUPPORTED_ACTION_VERSION,
  MultiValueActionComponent,
  SOLANA_ACTION_PREFIX,
  SignMessageVerificationErrorType,
  SingleValueActionComponent,
  checkSecurity,
  createSignMessageText,
  defaultBlinkSupportStrategy,
  fetchBlinksRegistryConfig,
  getBlinkSupportabilityMetadata,
  getExtendedBlinkState,
  getExtendedInterstitialState,
  getExtendedWebsiteState,
  isBlockchainSupported,
  isInterstitial,
  isParameterSelectable,
  isPatternAllowed,
  isProxified,
  isVersionSupported,
  mergeBlinkStates,
  parseSignMessageText,
  proxify,
  proxifyImage,
  setClientKey,
  setProxyUrl,
  unfurlUrlToBlinkApiUrl,
  verifySignMessageData
} from "./chunk-5XH3SWXK.js";

// src/BlinkContainer.tsx
import {
  useEffect,
  useMemo,
  useReducer,
  useState
} from "react";

// src/utils/type-guards.ts
var isSignTransactionError = (data) => !!data.error;
var isSignMessageError = isSignTransactionError;
var isPostRequestError = (data) => !!data.error;

// src/utils/validators.ts
var isURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// src/BlinkContainer.tsx
import { jsx } from "react/jsx-runtime";
var DisclaimerType = /* @__PURE__ */ ((DisclaimerType2) => {
  DisclaimerType2["BLOCKED"] = "blocked";
  DisclaimerType2["UNKNOWN"] = "unknown";
  return DisclaimerType2;
})(DisclaimerType || {});
var ExecutionType = /* @__PURE__ */ ((ExecutionType2) => {
  ExecutionType2["CHECK_SUPPORTABILITY"] = "CHECK_SUPPORTABILITY";
  ExecutionType2["INITIATE"] = "INITIATE";
  ExecutionType2["FINISH"] = "FINISH";
  ExecutionType2["FAIL"] = "FAIL";
  ExecutionType2["RESET"] = "RESET";
  ExecutionType2["SOFT_RESET"] = "SOFT_RESET";
  ExecutionType2["UNBLOCK"] = "UNBLOCK";
  ExecutionType2["BLOCK"] = "BLOCK";
  ExecutionType2["PARTIAL_UPDATE"] = "PARTIAL_UPDATE";
  return ExecutionType2;
})(ExecutionType || {});
var executionReducer = (state, action) => {
  switch (action.type) {
    case "CHECK_SUPPORTABILITY" /* CHECK_SUPPORTABILITY */:
      return {
        status: "checking-supportability",
        checkingSupportability: true
      };
    case "INITIATE" /* INITIATE */:
      return { status: "executing", executingAction: action.executingAction };
    case "FINISH" /* FINISH */:
      return {
        ...state,
        status: "success",
        successMessage: action.successMessage,
        errorMessage: null
      };
    case "FAIL" /* FAIL */:
      return {
        ...state,
        status: "error",
        errorMessage: action.errorMessage,
        successMessage: null
      };
    case "RESET" /* RESET */:
      return {
        status: "idle"
      };
    case "SOFT_RESET" /* SOFT_RESET */:
      return {
        ...state,
        executingAction: null,
        status: "idle",
        errorMessage: action.errorMessage,
        successMessage: null
      };
    case "BLOCK" /* BLOCK */:
      return {
        status: "blocked"
      };
    case "UNBLOCK" /* UNBLOCK */:
      return {
        status: "idle"
      };
    case "PARTIAL_UPDATE" /* PARTIAL_UPDATE */:
      return {
        ...state,
        generalMessage: action.message
      };
  }
};
var getOverallBlinkState = (blink, websiteUrl) => {
  const blinkState = getExtendedBlinkState(blink);
  const originalUrlData = websiteUrl ? isInterstitial(websiteUrl) : null;
  if (!originalUrlData) {
    return {
      blink: blinkState
    };
  }
  if (originalUrlData.isInterstitial) {
    return {
      blink: blinkState,
      origin: getExtendedInterstitialState(websiteUrl),
      originType: "interstitials"
    };
  }
  return {
    blink: blinkState,
    origin: getExtendedWebsiteState(websiteUrl),
    originType: "websites"
  };
};
var checkSecurityFromBlinkState = (state, normalizedSecurityLevel) => {
  const checkBlink = checkSecurity(
    state.blink,
    normalizedSecurityLevel.actions
  );
  if (!state.origin) {
    return checkBlink;
  }
  return checkBlink && checkSecurity(state.origin, normalizedSecurityLevel[state.originType]);
};
var DEFAULT_SECURITY_LEVEL = "only-trusted";
var BlinkContainer = ({
  blink: initialBlink,
  adapter,
  websiteUrl,
  websiteText,
  callbacks = EMPTY_OBJECT,
  securityLevel = DEFAULT_SECURITY_LEVEL,
  Layout,
  selector
}) => {
  const [blink, setBlink] = useState(initialBlink);
  const singleComponent = useMemo(() => selector?.(blink), [blink, selector]);
  const isPartialBlink = typeof selector === "function";
  const normalizedSecurityLevel = useMemo(() => {
    if (typeof securityLevel === "string") {
      return {
        websites: securityLevel,
        interstitials: securityLevel,
        actions: securityLevel
      };
    }
    return securityLevel;
  }, [securityLevel]);
  const [blinkState, setBlinkState] = useState(
    getOverallBlinkState(blink, websiteUrl)
  );
  const [supportability, setSupportability] = useState({
    isSupported: true
  });
  const overallState = useMemo(
    () => mergeBlinkStates(
      ...[blinkState.blink, blinkState.origin].filter(
        Boolean
      )
    ),
    [blinkState]
  );
  const isPassingSecurityCheck = isPartialBlink ? true : checkSecurityFromBlinkState(blinkState, normalizedSecurityLevel);
  const [executionState, dispatch] = useReducer(executionReducer, {
    status: isPartialBlink ? "idle" : "checking-supportability"
  });
  useEffect(() => {
    if (blink === initialBlink) {
      return;
    }
    setBlink(initialBlink);
    setBlinkState(getOverallBlinkState(initialBlink, websiteUrl));
    dispatch({
      type: isPartialBlink ? "RESET" /* RESET */ : "CHECK_SUPPORTABILITY" /* CHECK_SUPPORTABILITY */
    });
  }, [initialBlink, websiteUrl]);
  useEffect(() => {
    callbacks.onActionMount?.(
      initialBlink,
      websiteUrl ?? initialBlink.url,
      blinkState.blink
    );
  }, []);
  useEffect(() => {
    const liveDataConfig = blink.liveData_experimental;
    if (!liveDataConfig || !liveDataConfig.enabled || executionState.status !== "idle" || blink.isChained) {
      return;
    }
    let timeout;
    const fetcher = async () => {
      try {
        const newBlink = await blink.refresh();
        if (executionState.status === "idle") {
          setBlink(newBlink);
        }
      } catch (e) {
        console.error(
          `[@dialectlabs/blinks] Failed to fetch live data for blink ${blink.url}`
        );
        timeout = setTimeout(fetcher, liveDataConfig.delayMs);
      }
    };
    timeout = setTimeout(fetcher, liveDataConfig.delayMs);
    return () => {
      clearTimeout(timeout);
    };
  }, [blink, executionState.status, isPartialBlink]);
  useEffect(() => {
    const checkSupportability = async (blink2) => {
      if (blink2.isChained || executionState.status !== "checking-supportability") {
        return;
      }
      try {
        const supportability2 = await blink2.isSupported(adapter);
        setSupportability(supportability2);
      } finally {
        dispatch({
          type: overallState !== "malicious" && isPassingSecurityCheck ? "RESET" /* RESET */ : "BLOCK" /* BLOCK */
        });
      }
    };
    checkSupportability(blink);
  }, [
    blink,
    adapter,
    executionState.status,
    overallState,
    isPassingSecurityCheck
  ]);
  const execute = async (component, params) => {
    if (params) {
      if (component instanceof FormActionComponent) {
        Object.entries(params).forEach(
          ([name, value]) => component.setValue(value, name)
        );
      }
      if (component instanceof MultiValueActionComponent) {
        component.setValue(params[component.parameter.name]);
      }
      if (component instanceof SingleValueActionComponent) {
        const incomingValues = params[component.parameter.name];
        const value = typeof incomingValues === "string" ? incomingValues : incomingValues[0];
        component.setValue(value);
      }
    }
    const newBlinkState = getOverallBlinkState(blink, websiteUrl);
    const newIsPassingSecurityCheck = checkSecurityFromBlinkState(
      newBlinkState,
      normalizedSecurityLevel
    );
    if ((newBlinkState.blink !== blinkState.blink || newBlinkState.origin !== blinkState.origin) && !newIsPassingSecurityCheck) {
      setBlinkState(newBlinkState);
      dispatch({ type: "BLOCK" /* BLOCK */ });
      callbacks.onActionCancel?.(blink, component, "security-state-changed");
      return;
    }
    if (component.type === "inline-link") {
      if (isURL(component.href)) {
        return {
          type: "external-link",
          data: {
            externalLink: component.href
          },
          onNext: () => {
          },
          onCancel: () => {
          }
        };
      }
      return;
    }
    dispatch({ type: "INITIATE" /* INITIATE */, executingAction: component });
    const context = {
      action: component.parent,
      actionType: blinkState.blink,
      originalUrl: websiteUrl ?? component.parent.url,
      triggeredLinkedAction: component
    };
    try {
      const account = await adapter.connect(context);
      if (!account) {
        dispatch({ type: "RESET" /* RESET */ });
        callbacks?.onActionCancel?.(blink, component, "wallet-not-connected");
        return;
      }
      const response = await component.post(account).catch((e) => ({ error: e.message }));
      if (isPostRequestError(response)) {
        dispatch({
          type: "SOFT_RESET" /* SOFT_RESET */,
          errorMessage: isPostRequestError(response) ? response.error : "Transaction data missing"
        });
        callbacks.onActionError?.(blink, component, "post-request-error");
        return;
      }
      if (response.lifecycle?.executing) {
        dispatch({
          type: "PARTIAL_UPDATE" /* PARTIAL_UPDATE */,
          message: response.lifecycle.executing.message
        });
      }
      const chain = async (signature) => {
        if (!response.links?.next && !response.lifecycle?.success) {
          dispatch({
            type: "FINISH" /* FINISH */,
            successMessage: response.message
          });
          callbacks.onActionComplete?.(blink, component, signature);
          return;
        }
        if (response.type === "message" && !signature) {
          dispatch({
            type: "SOFT_RESET" /* SOFT_RESET */,
            errorMessage: response.lifecycle?.error?.message ?? "Missing signature for message"
          });
          callbacks.onActionError?.(
            blink,
            component,
            "message-signature-missing"
          );
          return;
        }
        const chainData = response.type === "message" ? {
          signature,
          account,
          state: response.state,
          data: response.data
        } : {
          signature,
          account
        };
        const nextBlink = response.links?.next ? await blink.chain(
          response.links.next,
          chainData,
          response.lifecycle?.success
        ) : blink.safeInlineChain(response.lifecycle?.success);
        if (!nextBlink || isPartialBlink && !selector?.(nextBlink)) {
          dispatch({
            type: "FINISH" /* FINISH */,
            successMessage: response.message
          });
          callbacks.onActionComplete?.(blink, component, signature);
          return;
        }
        setBlink(nextBlink);
        dispatch({ type: "RESET" /* RESET */ });
        callbacks.onActionChain?.(
          blink,
          nextBlink,
          component,
          response.type,
          signature
        );
      };
      if (response.type === "transaction" || !response.type) {
        const signResult = await adapter.signTransaction(
          response.transaction,
          context
        );
        if (!signResult || isSignTransactionError(signResult)) {
          dispatch({
            type: "SOFT_RESET" /* SOFT_RESET */,
            errorMessage: signResult.error ?? "Unable to sign transaction"
          });
          callbacks.onActionCancel?.(
            blink,
            component,
            "transaction-sign-cancel"
          );
          return;
        }
        const confirmationResult = await adapter.confirmTransaction(signResult.signature, context).then(() => ({ success: true })).catch((e) => ({ success: false, message: e.message }));
        if (!confirmationResult.success) {
          dispatch({
            type: "SOFT_RESET" /* SOFT_RESET */,
            errorMessage: response.lifecycle?.error?.message ?? confirmationResult.message ?? "Unknown error, please try again"
          });
          callbacks.onActionError?.(
            blink,
            component,
            "transaction-confirmation-failed",
            signResult.signature
          );
          return;
        }
        await chain(signResult.signature);
        return;
      }
      if (response.type === "message") {
        const signResult = await adapter.signMessage(response.data, context);
        if (!signResult || isSignMessageError(signResult)) {
          dispatch({ type: "RESET" /* RESET */ });
          callbacks.onActionCancel?.(blink, component, "message-sign-cancel");
          return;
        }
        await chain(signResult.signature);
        return;
      }
      if (response.type === "post") {
        await chain();
        return;
      }
      if (response.type === "external-link") {
        if (isURL(response.externalLink)) {
          return {
            type: "external-link",
            data: {
              externalLink: response.externalLink
            },
            onNext: () => chain(),
            onCancel: () => chain()
          };
        }
        await chain();
        return;
      }
    } catch (e) {
      dispatch({
        type: "SOFT_RESET" /* SOFT_RESET */,
        errorMessage: e.message ?? "Unknown error, please try again"
      });
      callbacks.onActionError?.(blink, component, "unknown-error");
    }
  };
  const disclaimer = useMemo(() => {
    if (overallState === "malicious") {
      return {
        type: "blocked" /* BLOCKED */,
        ignorable: isPassingSecurityCheck,
        hidden: executionState.status !== "blocked" && executionState.status !== "checking-supportability",
        onSkip: () => dispatch({ type: "UNBLOCK" /* UNBLOCK */ })
      };
    }
    if (overallState === "unknown") {
      return {
        type: "unknown" /* UNKNOWN */,
        ignorable: isPassingSecurityCheck
      };
    }
    return null;
  }, [executionState.status, isPassingSecurityCheck, overallState]);
  const blinkCaption = useMemo(() => {
    const error = executionState.errorMessage ?? blink.error;
    const generalMessage = executionState.generalMessage ?? blink.message;
    if (error) {
      return { type: "error", text: error };
    }
    if (executionState.successMessage) {
      return { type: "success", text: executionState.successMessage };
    }
    if (generalMessage) {
      return { type: "default", text: generalMessage };
    }
    return null;
  }, [
    executionState.errorMessage,
    executionState.successMessage,
    executionState.generalMessage,
    blink.error,
    blink.message
  ]);
  return /* @__PURE__ */ jsx(
    Layout,
    {
      securityState: overallState,
      websiteUrl,
      websiteText,
      blink,
      component: singleComponent,
      caption: blinkCaption,
      executionStatus: executionState.status,
      executingAction: executionState.executingAction,
      executeFn: execute,
      disclaimer,
      supportability,
      id: blink.id
    }
  );
};
export {
  AbstractActionComponent,
  BlinkInstance as Action,
  BlinksRegistry as ActionsRegistry,
  BlinksURLMapper as ActionsURLMapper,
  BASELINE_BLINK_BLOCKCHAIN_IDS as BASELINE_ACTION_BLOCKCHAIN_IDS,
  BASELINE_ACTION_VERSION,
  BASELINE_BLINK_BLOCKCHAIN_IDS,
  BLINK_CLIENT_KEY_HEADER,
  BlinkContainer,
  BlinkInstance,
  BlinksRegistry,
  BlinksURLMapper,
  BlockchainIds,
  ButtonActionComponent,
  DEFAULT_SUPPORTED_BLOCKCHAIN_IDS,
  DisclaimerType,
  ExecutionType,
  FormActionComponent,
  MAX_SUPPORTED_ACTION_VERSION,
  MultiValueActionComponent,
  SOLANA_ACTION_PREFIX,
  SignMessageVerificationErrorType,
  SingleValueActionComponent,
  checkSecurity,
  createSignMessageText,
  defaultBlinkSupportStrategy as defaultActionSupportStrategy,
  defaultBlinkSupportStrategy,
  fetchBlinksRegistryConfig as fetchActionsRegistryConfig,
  fetchBlinkList,
  fetchMetadata,
  getBlinkSupportabilityMetadata,
  getExtendedBlinkState as getExtendedActionState,
  getExtendedBlinkState,
  getExtendedInterstitialState,
  getExtendedWebsiteState,
  isBlockchainSupported,
  isInterstitial,
  isParameterSelectable,
  isPatternAllowed,
  isProxified,
  isVersionSupported,
  mergeBlinkStates as mergeActionStates,
  mergeBlinkStates,
  parseSignMessageText,
  proxify,
  proxifyImage,
  setClientKey,
  setProxyUrl,
  unfurlUrlToBlinkApiUrl as unfurlUrlToActionApiUrl,
  unfurlUrlToBlinkApiUrl,
  useAction,
  useBlinkApiUrl as useActionApiUrl,
  useBlinksRegistryInterval as useActionsRegistryInterval,
  useBlink,
  useBlinkApiUrl,
  useBlinkList,
  useBlinksRegistryInterval,
  useMetadata,
  verifySignMessageData
};
